import React, { useState, useEffect, useContext } from "react"
import { linkResolver } from "utils/linkResolver"
import { graphql, navigate } from "gatsby"
import Cookies from "js-cookie"
import styled from "styled-components"
import Checkbox from "lib/Checkbox"
import Icon from "lib/Icon"
import Content from "components/Content"
import {
  ButtonGroup,
  Textarea,
  Form,
  FormContext,
  FormField,
  Button,
  ValidationInput,
} from "@clevertrack/shared"
import { createHubspotLeadModel, addLeadToHubspot } from "./service"
import { getDictionary } from "./dictionary"
import Accordion from "lib/Accordion"
import tw from "twin.macro"

const StyledHubspotLeadForm = styled(Form)`
  padding: 2rem 0 0;
  position: relative;

  .consent {
    margin: 2rem 0;
    align-items: center;
    p {
      margin-top: -0.8rem;
      ${tw`text-lg md:text-xl`}

      ${(props) => props.theme.media.tablet_landscape_up`
        margin-top: -0.4rem;
      `}
    }

    &.-missing {
      color: ${(props) => props.theme.colors.danger};
    }
  }

  .toggle-terms-link {
    cursor: pointer;
    ${tw`text-brand-500 mb-8 text-right text-lg md:text-xl`}
    // float: right;
    display: block;
    align-items: center;

    svg {
      width: 1rem;
      height: 1rem;
      margin-left: 0.5rem;
      transition: ease-out 150ms all;
      transform: rotateZ(0);
    }

    &.-toggled {
      svg {
        transform: rotateZ(-180deg);
      }
    }
  }

  ${(props) => props.theme.media.tablet_landscape_up`
    margin: 0 auto 4rem;

    .wrapper {
      position: relative;
      z-index: 10;
      background: white;
      padding: 2rem;
      filter: drop-shadow(rgba(50, 50, 93, 0.5) 20px 20px 40px);
      max-width: 60rem;
      margin: 0 auto;
    }
  `}
`

const StyledResult = styled.article`
  font-family: ${(props) => props.theme.fontStacks.light};

  &.success {
    text-align: center;
    svg {
      fill: ${(props) => props.theme.colors.success};
    }
  }

  &.error {
    svg {
      fill: ${(props) => props.theme.colors.danger};
    }
  }
`

const StyledHubspotLeadFormFields = styled.article`
  padding: 0 2rem;

  ${(props) => props.theme.media.tablet_landscape_up`
    padding: 0;
  `}
`

const StyledLegalContent = styled.article`
  margin: 2rem 2rem 0;
  padding: 1px 2rem 0;
  background: ${(props) => props.theme.colors.grayLighter};
  text-align: left;

  ${(props) => props.theme.media.tablet_landscape_up`
    margin: 2rem auto 0;
  `}

  p {
    font-size: 1.4rem;
    line-height: 1.4;

    &:first-of-type {
      margin-top: 0;
    }
  }
`

const HoneypotField = styled.div`
  position: absolute;
  left: -9999px;
  top: -9999px;
  opacity: 0;
  height: 0;
  width: 0;
  z-index: -1;
  overflow: hidden;
  pointer-events: none;
`

function HubspotLeadModuleForm({ onValidate, onChange, language, ...props }) {
  const {
    state: { fields },
  } = useContext(FormContext)

  const t = getDictionary(language)

  useEffect(() => {
    onValidate(fields)
  }, [fields])

  return (
    <StyledHubspotLeadFormFields>
      <FormField label={t.fieldEmail} validationKey="email">
        <ValidationInput
          id="email"
          type="email"
          placeholder={t.fieldEmailPlaceholder}
          onChange={(e) => onChange("email", e.target.value)}
          required
        />
      </FormField>
      <FormField label={t.fieldFirstName} validationKey="firstname">
        <ValidationInput
          id="firstname"
          type="text"
          placeholder={t.fieldFirstNamePlaceholder}
          onChange={(e) => onChange("firstname", e.target.value)}
          required
        />
      </FormField>
      <FormField label={t.fieldCompany} validationKey="company">
        <ValidationInput
          id="company"
          type="text"
          placeholder={t.fieldCompanyPlaceholder}
          onChange={(e) => onChange("company", e.target.value)}
          required
        />
      </FormField>
      <FormField label={t.fieldPhone} validationKey="phone">
        <ValidationInput
          id="phone"
          type="phone"
          placeholder={t.fieldPhonePlaceholder}
          onChange={(e) => onChange("phone", e.target.value)}
          required
        />
      </FormField>
      <FormField label={t.fieldMessage}>
        <Textarea
          id="message"
          placeholder={t.fieldMessagePlaceholder}
          onChange={(e) => onChange("message", e.target.value)}
        />
      </FormField>
    </StyledHubspotLeadFormFields>
  )
}

const HubspotLeadModule = ({
  hs_form_id,
  hs_portal_id,
  hs_retry_btn_text,
  hs_submit_btn_text,
  hs_success_message,
  hs_error_message,
  language,
  confirmation_page,
  meta,
  use_theme,
  theme,
  ...props
}) => {
  const t = getDictionary(language)

  const context = {
    hutk: Cookies.get("hubspotutk"),
    pageUri: `${meta.host}${meta.uri}`,
    pageName: `${meta.title}`,
  }
  const [model, setModel] = useState({
    email: "",
    firstname: "",
    company: "",
    phone: "",
    message: "",
    website: "", // Honeypot field
  })
  const [invalidFields, setInvalidFields] = useState([])
  const [consentCommunication, setConsentCommunication] = useState(false)
  const [consentProcess, setConsentProcess] = useState(false)
  const [toggleTerms, setToggleTerms] = useState(false)
  const [result, setResult] = useState(null)

  function onValidateHandler(fields) {
    setInvalidFields(fields)
  }

  function onChangeHandler(name, value) {
    const newModel = {
      ...model,
      [name]: value,
    }
    setModel(newModel)
  }

  async function onSubmitHandler(e) {
    e.preventDefault()

    // Honeypot check - if the website field is filled, it's likely a bot
    if (model.website) {
      // Silently reject the form but show success to the bot
      setResult("success")
      return
    }

    // Proceed with normal form submission
    const hubspotFields = []
    // Filter out the honeypot field
    Object.keys(model).forEach((value) => {
      if (value !== "website") {
        hubspotFields.push({
          name: value,
          value: model[value],
        })
      }
    })

    const submissionModel = createHubspotLeadModel(
      hubspotFields,
      context,
      consentProcess,
      consentCommunication
    )

    const r = await addLeadToHubspot(
      hs_portal_id,
      hs_form_id,
      submissionModel
    )

    if (r.status === 200) {
      setResult("success")
      setTimeout(() => {
        navigate(linkResolver(confirmation_page.document))
      }, 3000)
    } else {
      setResult("error")
    }
  }

  function onTermsToggleHandler(e) {
    e.stopPropagation()
    e.preventDefault()
    setToggleTerms((prev) => !prev)
  }

  return (
    <StyledHubspotLeadForm
      onSubmit={onSubmitHandler}
    >
      <div className="wrapper">
        {result !== null ? (
          result === "error" ? (
            <StyledResult className="error">
              <Icon icon="times-circle" size="xl" />
              <Content content={hs_error_message} />
              <Button onClick={() => setResult(null)}>
                {hs_retry_btn_text}
              </Button>
            </StyledResult>
          ) : (
            <StyledResult className="success">
              <Icon icon="check-circle" size="xl" />
              <Content content={hs_success_message} />
            </StyledResult>
          )
        ) : (
          <>
            <HubspotLeadModuleForm
              onChange={onChangeHandler}
              onValidate={onValidateHandler}
              language={language}
              {...props}
            />
            <HoneypotField>
              <ValidationInput
                id="website"
                name="website"
                type="text"
                autoComplete="off"
                tabIndex="-1"
                aria-hidden="true"
                onChange={(e) => onChangeHandler("website", e.target.value)}
              />
            </HoneypotField>
            <StyledLegalContent>
              <Checkbox
                onChange={(value) => setConsentCommunication(value)}
                checked={consentCommunication}
                className={`consent`}
              >
                <p>{t.consentCommunication}</p>
              </Checkbox>
              <Checkbox
                onChange={(value) => setConsentProcess(value)}
                checked={consentProcess}
                required
                className={`consent${consentProcess ? "" : " -missing"}`}
              >
                <p>{t.consentProcess}</p>
              </Checkbox>
              <a
                href
                className={`toggle-terms-link${toggleTerms ? " -toggled" : ""}`}
                onClick={onTermsToggleHandler}
              >
                {t.toggleTermsLink}
                <Icon icon="chevron-down" size="auto" />
              </a>
              <Accordion toggled={toggleTerms}>
                <div tw="text-lg pb-4">
                  <p><small>{t.termsP1}</small></p>
                  <p><small>{t.termsP2}</small></p>
                  <p><small>{t.termsP3}</small></p>
                </div>
              </Accordion>
            </StyledLegalContent>
            <ButtonGroup position="center">
              <Button variant="primary" disabled={!consentProcess}>
                {hs_submit_btn_text}
              </Button>
            </ButtonGroup>
          </>
        )}
      </div>
    </StyledHubspotLeadForm>
  )
}

export default HubspotLeadModule

HubspotLeadModule.defaultProps = {}
HubspotLeadModule.propTypes = {}

export const query = graphql`
  fragment HubspotModuleFragment on PrismicLeadModuleDataBodyHubspot {
    slice_type
    primary {
      hs_form_id
      hs_portal_id
      hs_retry_btn_text
      hs_submit_btn_text
      language
      hs_success_message {
        richText
        text
      }
      hs_error_message {
        richText
        text
      }
      confirmation_page {
        document {
          ... on PrismicPage {
            uid
            type
            lang
          }
        }
      }
    }
  }
`
